import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { Dimmer, Loader } from "semantic-ui-react";
import { useTRPC } from "@iscr/xAppLib/context/QueryProvider";
import Alert from "@iscr/views/NUI/Alert";
import { AsyncButton } from "@iscr/views/UIelems/AsyncButton";
import CopyField from "@iscr/xAppLib/UIelems/CopyField";
import { checkStatus, StatusChecklist } from "@iscr/xAppLib/UIelems/StatusChecklist";
const yesNo = (value) => (value ? "Yes" : "No");
function getSummary({ onboarded, activated, migrationEligibility }) {
    if (onboarded) {
        return activated
            ? { type: "success", header: "Onboarded and activated", content: "They have an active pharmacy profile." }
            : {
                type: "warning",
                header: "Onboarded, not activated",
                content: "Their identity hasn't been verified in store yet.",
            };
    }
    if (migrationEligibility === null || migrationEligibility === void 0 ? void 0 : migrationEligibility.canMigrate) {
        return {
            type: "success",
            header: "Can be migrated",
            content: "They can transfer their MedAdvisor profile from the pharmacy setup in the app.",
        };
    }
    if (migrationEligibility === null || migrationEligibility === void 0 ? void 0 : migrationEligibility.emailExists) {
        return {
            type: "warning",
            header: "Can't be migrated yet",
            content: "They're in MedAdvisor but haven't activated their profile. Ask them to activate it in MedAdvisor, then migrate across from the pharmacy setup in the app.",
        };
    }
    return {
        type: "info",
        header: "No MedAdvisor account found",
        list: [
            "Confirm their email address with them.",
            "If it matches the login email below, ask them to log in to the old Priceline app again by typing their email and password, not with an automatic login. This sometimes fixes it.",
        ],
    };
}
function getChecks(profile) {
    const { onboarded, maPatientId, activated, migrated, migrationDate, migrationEligibility } = profile;
    if (onboarded) {
        return [
            {
                key: "onboarded",
                label: "Onboarded",
                description: maPatientId && (_jsx(CopyField, { inline: true, val: maPatientId, label: `MedAdvisor ID ${maPatientId}` })),
                value: "Yes",
                status: "pass",
            },
            {
                key: "activated",
                label: "Activated (identity verified)",
                value: yesNo(activated),
                status: checkStatus(activated),
            },
            {
                key: "migrated",
                label: "Migrated from MedAdvisor",
                value: migrated && migrationDate ? moment(migrationDate).format("D MMM YYYY") : yesNo(migrated),
                status: checkStatus(migrated, "neutral"),
            },
        ];
    }
    const { emailExists = false, isVerifiedUser = false, isEmailVerified = false } = migrationEligibility !== null && migrationEligibility !== void 0 ? migrationEligibility : {};
    return [
        { key: "onboarded", label: "Onboarded", value: "No", status: "neutral" },
        {
            key: "exists",
            label: "MedAdvisor account found",
            value: yesNo(emailExists),
            status: checkStatus(emailExists),
        },
        {
            key: "verified",
            label: "Activated in MedAdvisor",
            value: yesNo(isVerifiedUser),
            status: checkStatus(isVerifiedUser, emailExists ? "fail" : "neutral"),
        },
        {
            key: "email-verified",
            label: "Email verified in MedAdvisor",
            description: "Doesn't affect migration",
            value: yesNo(isEmailVerified),
            status: "info",
        },
    ];
}
/** Summary banner and checklist for a pharmacy profile, without fetching, so it can be shown anywhere */
export function PharmacyProfileDetails({ profile }) {
    const { type, header, content, list } = getSummary(profile);
    return (_jsxs(_Fragment, { children: [_jsx(Alert, { success: type === "success", warning: type === "warning", info: type === "info", header: header, content: content, list: list }), profile.email && (_jsxs("p", { className: "text-gray-600", children: ["Login email: ", _jsx(CopyField, { inline: true, val: profile.email })] })), _jsx(StatusChecklist, { items: getChecks(profile) })] }));
}
/**
 * Staff view of a Priceline user's pharmacy profile: their onboarding status, or, if they aren't onboarded,
 * whether they can migrate from MedAdvisor (the same check the app runs in the pharmacy setup).
 */
export function PharmacyProfile({ uid }) {
    const trpc = useTRPC();
    const { data, error, isPending, isFetching, refetch, dataUpdatedAt, errorUpdatedAt } = useQuery(trpc.pricelineUserAdmin.pharmacyProfile.queryOptions({ uid }, { gcTime: 0, retry: false }));
    const canCheckAgain = !isPending && (!!error || !(data === null || data === void 0 ? void 0 : data.onboarded));
    return (_jsxs("div", { className: "relative min-h-[8rem] text-sm", children: [_jsx(Dimmer, { active: isFetching, inverted: true, children: _jsx(Loader, { content: "Loading pharmacy profile" }) }), error ? (_jsx(Alert, { error: true, header: "Couldn't check", content: error.message })) : (data && _jsx(PharmacyProfileDetails, { profile: data })), canCheckAgain && (_jsxs("div", { className: "flex items-center justify-between mt-4 text-xs text-gray-500", children: [_jsxs("span", { children: ["Checked ", moment(Math.max(dataUpdatedAt, errorUpdatedAt)).format("h:mma")] }), _jsx(AsyncButton, { size: "small", icon: "refresh", content: "Check again", onClick: () => refetch() })] }))] }));
}
